/*#
# xr-frames

Reference frames for spatial XR UI. "Stable" in XR isn't one thing — there are
several distinct frames a piece of UI can be anchored to, and WebXR only hands
you the two extremes (world and head) plus the *sensed* points (hands), leaving
the comfortable middle (torso, neck) to be reconstructed. `XrFrames` maintains a
`TransformNode` for each so scene UI can just parent to the one it wants:

| Frame | Stable relative to… | Use |
|-------|---------------------|-----|
| `world` | the play space | a hologram on a table |
| `rig` | the locomotion rig (vehicle) | a piloting HUD that flies with you |
| `body` | your torso (head x/z + **damped** yaw) | waist inventory, over-shoulder backpack |
| `neck` | your neck pivot | UI you can look *past* |
| `face` | your eyes (head-locked) | a reticle, a vignette, sunglasses |

`body` and `neck` aren't measured by any sensor in a head-plus-hands rig, so they
are *inferred*: `body` low-passes the head's yaw (sustained turns move it, quick
glances don't) and sits at floor level under the head; `neck` is the head pose
pushed down-and-back to the pivot the head swings around. Hand frames come from
the controllers/`XRHand` joints (sensed) and are added separately.

The yaw-damping, gaze-reveal and angle math are pure (no Babylon) so they're unit
tested in `xr-frames.test.ts`.

## Example

XR-only (needs an immersive session). Spatial UI parents to a *frame* — `world`, `rig`, `body`,
`neck`, or `face` — so it stays where it should as you move:

```javascript
import { XrFrames } from 'tosijs-3d'
// XrFrames maintains a TransformNode per frame; parent a panel to `body` and it follows your torso
// (damped yaw) but not your gaze. See <tosi-b3d-panel> / frame-panel for the assembled spatial UI.
```
*/
/*{ "parent": "b3d-panel", "order": 20 }*/

import * as BABYLON from '@babylonjs/core'

// ---------------------------------------------------------------------------
// Pure math (no Babylon types) — unit tested.
// ---------------------------------------------------------------------------

export interface Vec3 {
  x: number
  y: number
  z: number
}

export type FrameName =
  | 'world'
  | 'rig'
  | 'eye'
  | 'body'
  | 'neck'
  | 'face'
  | 'left-hand'
  | 'right-hand'

/** Shortest signed angle to rotate from `a` to `b`, in (-π, π]. */
export function angleDelta(a: number, b: number): number {
  let d = b - a
  while (d > Math.PI) d -= 2 * Math.PI
  while (d <= -Math.PI) d += 2 * Math.PI
  return d
}

/** Yaw (about +Y) so a frame's local +Z points horizontally from `from` toward
 * `to`. Used to face an entity-pinned frame (NPC dialogue, nameplates) at the
 * player: with the frame facing you, its local −X is your screen-right and +X
 * your screen-left, so left/right balloon layout stays put as you move. */
export function facingYaw(from: Vec3, to: Vec3): number {
  return Math.atan2(to.x - from.x, to.z - from.z)
}

/** Exponentially damp a yaw toward `target` at `rate` per second. A `deadband`
 * (radians) holds the value still for small offsets, so quick head glances don't
 * drag the body frame — only sustained turns past the deadband move it. Returns
 * the new yaw (frame-rate independent via 1 − e^(−rate·dt)). */
export function dampYaw(
  current: number,
  target: number,
  dt: number,
  rate: number,
  deadband = 0
): number {
  const d = angleDelta(current, target)
  if (Math.abs(d) <= deadband) return current
  // Pull the target in by the deadband so there's no jump when it engages.
  const goal = d > 0 ? d - deadband : d + deadband
  const t = dt <= 0 ? 0 : 1 - Math.exp(-rate * dt)
  return current + goal * t
}

/** Reveal factor 0..1 for gaze-anchored UI: 1 when the head looks straight at the
 * anchor, ramping to 0 as the angle widens. `cosStart`/`cosFull` are the cosines
 * of the half-angles where the reveal begins and completes (cosFull > cosStart).
 * Both vectors may be unnormalised. */
export function gazeReveal(
  headForward: Vec3,
  toAnchor: Vec3,
  cosStart: number,
  cosFull: number
): number {
  const fl = Math.hypot(headForward.x, headForward.y, headForward.z) || 1
  const al = Math.hypot(toAnchor.x, toAnchor.y, toAnchor.z) || 1
  const c =
    (headForward.x * toAnchor.x +
      headForward.y * toAnchor.y +
      headForward.z * toAnchor.z) /
    (fl * al)
  if (c <= cosStart) return 0
  if (c >= cosFull) return 1
  return (c - cosStart) / (cosFull - cosStart)
}

// ---------------------------------------------------------------------------
// Babylon-side frame manager.
// ---------------------------------------------------------------------------

export interface XrFramesOptions {
  /** Per-second damping rate pulling the body yaw toward the head yaw. */
  bodyYawRate?: number
  /** Radians of head-yaw offset ignored before the body follows (glance vs turn). */
  bodyYawDeadband?: number
  /** Eye→neck-pivot offset in head-local space (down + back). */
  neckOffset?: BABYLON.Vector3
  /**
   * FLAT mode — no session: the frames are derived from the camera's WORLD
   * pose, whatever kind of camera it is. See `XrFrames.flat`.
   */
  flat?: boolean
  /** Flat only: how far below the camera the floor (the `body` frame) sits. */
  eyeHeight?: number
}

const FORWARD = new BABYLON.Vector3(0, 0, 1)

/**
 * Maintains a `TransformNode` per reference frame. Construct once an XR session
 * is live (the camera must be parented to `rig`), call `update(dt)` every XR
 * frame, and `dispose()` on exit. Parent scene UI to `frames.body` etc.
 *
 * **Or flat**, with `XrFrames.flat(scene, camera)` (tosijs-3d#81): most of these
 * frames depend only on a camera's position and orientation, which a flat
 * camera has. `eye`, `body`, `neck` and `face` follow the view; `world` and
 * `rig` sit at the origin; the HAND frames stay disabled, because a monitor has
 * no hands — a panel that wants one declares a flat fallback instead.
 */
export class XrFrames {
  /**
   * Frames for a flat camera — no session, any camera type. The frames are
   * derived from its world pose each `update`; `setCamera` follows an
   * active-camera change. The rig is created here and disposed with the rest.
   */
  static flat(
    scene: BABYLON.Scene,
    camera: BABYLON.Camera,
    opts: Omit<XrFramesOptions, 'flat'> = {}
  ): XrFrames {
    const rig = new BABYLON.TransformNode('xr-frame-flat-rig', scene)
    const frames = new XrFrames(scene, rig, camera as BABYLON.TargetCamera, {
      ...opts,
      flat: true,
    })
    frames.ownsRig = true
    return frames
  }

  private flatMode: boolean
  private eyeHeight: number
  private ownsRig = false
  private _pos = new BABYLON.Vector3()
  readonly world: BABYLON.TransformNode
  readonly rig: BABYLON.TransformNode
  /** At your actual head POSITION but with RIG yaw (not head rotation). The
   * stable anchor for around-you HUD panels: they ride your eye through any
   * rig head-compensation and don't spin when you glance, only when you turn. */
  readonly eye: BABYLON.TransformNode
  readonly body: BABYLON.TransformNode
  readonly neck: BABYLON.TransformNode
  readonly face: BABYLON.TransformNode
  /** Hand/wrist frames — follow the controller grip (sensed), disabled while no
   * controller is connected for that hand. Wire with `attachInput`. */
  readonly leftHand: BABYLON.TransformNode
  readonly rightHand: BABYLON.TransformNode

  private inputObs: { obs: any; cb: any }[] = []
  /** Local yaw (radians) applied to the `eye` frame on top of the rig yaw. The
   * piloting rig is yaw-RECENTERED so your head points along the entity; set this
   * to that recenter so eye-anchored panels align with where you actually look,
   * not the rig's raw yaw. */
  eyeYawOffset = 0
  private cam: BABYLON.TargetCamera
  private bodyYaw = 0
  private seeded = false
  private bodyYawRate: number
  private bodyYawDeadband: number
  private neckOffset: BABYLON.Vector3

  // Scratch — never allocate per frame.
  private _q = new BABYLON.Quaternion()
  private _m = new BABYLON.Matrix()
  private _fwd = new BABYLON.Vector3()
  private _v = new BABYLON.Vector3()

  constructor(
    scene: BABYLON.Scene,
    rig: BABYLON.TransformNode,
    camera: BABYLON.TargetCamera,
    opts: XrFramesOptions = {}
  ) {
    this.cam = camera
    this.rig = rig
    this.bodyYawRate = opts.bodyYawRate ?? 6
    this.bodyYawDeadband = opts.bodyYawDeadband ?? 0.35 // ~20°
    this.neckOffset = opts.neckOffset ?? new BABYLON.Vector3(0, -0.12, -0.1)
    this.flatMode = opts.flat === true
    this.eyeHeight = opts.eyeHeight ?? 1.6

    // world: fixed at the play-space origin (scene root, no parent).
    this.world = new BABYLON.TransformNode('xr-frame-world', scene)
    // body / neck: ride the rig (so locomotion carries them) and are updated
    // each frame from the head pose in the rig's local space.
    this.body = new BABYLON.TransformNode('xr-frame-body', scene)
    this.neck = new BABYLON.TransformNode('xr-frame-neck', scene)
    this.eye = new BABYLON.TransformNode('xr-frame-eye', scene)
    this.body.parent = rig
    this.neck.parent = rig
    this.eye.parent = rig // local yaw = eyeYawOffset (rig-recenter correction)
    this.body.rotationQuaternion = new BABYLON.Quaternion()
    this.neck.rotationQuaternion = new BABYLON.Quaternion()
    this.eye.rotationQuaternion = new BABYLON.Quaternion()
    // face: head-locked.
    this.face = new BABYLON.TransformNode('xr-frame-face', scene)
    this.face.parent = camera
    // hands: world-space, posed each frame from the controller grips.
    this.leftHand = new BABYLON.TransformNode('xr-frame-left-hand', scene)
    this.rightHand = new BABYLON.TransformNode('xr-frame-right-hand', scene)
    this.leftHand.rotationQuaternion = new BABYLON.Quaternion()
    this.rightHand.rotationQuaternion = new BABYLON.Quaternion()
    this.leftHand.setEnabled(false)
    this.rightHand.setEnabled(false)
  }

  /** Wire hand/wrist frames to the WebXR input so they track the controller grip
   * for each hand. Pass `xrHelper.input`. Safe to call once. */

  attachInput(input: any): void {
    if (input == null) return

    const added = (controller: any) => {
      const hand = controller?.inputSource?.handedness
      const bind = () => {
        const grip = controller.grip ?? controller.pointer
        if (grip == null) return
        // PARENT the hand frame to the grip — rigid, so the panel doesn't jiggle
        // against the rendered controller (copying the pose each frame lagged it).
        const frame = hand === 'left' ? this.leftHand : this.rightHand
        if (hand !== 'left' && hand !== 'right') return
        frame.parent = grip
        frame.position.set(0, 0, 0)
        ;(frame.rotationQuaternion as BABYLON.Quaternion).set(0, 0, 0, 1)
        frame.setEnabled(true)
      }
      bind()
      controller?.onMotionControllerInitObservable?.add(bind)
    }

    const removed = (controller: any) => {
      const hand = controller?.inputSource?.handedness
      // Detach BEFORE Babylon disposes the grip (it would recurse to our child).
      const frame =
        hand === 'left'
          ? this.leftHand
          : hand === 'right'
          ? this.rightHand
          : null
      if (frame) {
        frame.parent = null
        frame.setEnabled(false)
      }
    }
    input.onControllerAddedObservable.add(added)
    input.onControllerRemovedObservable.add(removed)
    input.controllers?.forEach(added) // any already-connected
    this.inputObs.push(
      { obs: input.onControllerAddedObservable, cb: added },
      { obs: input.onControllerRemovedObservable, cb: removed }
    )
  }

  /** Resolve a frame node by name (for config that names a frame as a string). */
  get(name: FrameName): BABYLON.TransformNode {
    if (name === 'left-hand') return this.leftHand
    if (name === 'right-hand') return this.rightHand
    return this[name]
  }

  /** Follow a different camera (flat: the active camera changed). */
  setCamera(camera: BABYLON.Camera): void {
    this.cam = camera as BABYLON.TargetCamera
    this.face.parent = camera
  }

  /**
   * FLAT: every frame from the camera's WORLD pose. `getDirection` and
   * `globalPosition` work for any camera — an ArcRotate has no
   * rotationQuaternion, a FreeCamera usually keeps Euler angles — which is why
   * this cannot share the XR path's local-pose reads.
   */
  private updateFlat(): void {
    const cam = this.cam
    // This runs BEFORE the render that would refresh the camera's matrix, so
    // read a current one — otherwise every flat panel trails the view by a
    // frame. Cached when the camera has not moved.
    cam.computeWorldMatrix()
    const p = this._pos.copyFrom(cam.globalPosition)
    const fwd = cam.getDirection(FORWARD)
    const yaw = Math.atan2(fwd.x, fwd.z)
    // Eye: the view's position with its YAW, not its pitch — so a panel at
    // elevation 0 stays level however far you tilt to look at the ground.
    this.eye.position.copyFrom(p)
    BABYLON.Quaternion.RotationYawPitchRollToRef(
      yaw,
      0,
      0,
      this.eye.rotationQuaternion as BABYLON.Quaternion
    )
    this.body.position.set(p.x, p.y - this.eyeHeight, p.z)
    ;(this.body.rotationQuaternion as BABYLON.Quaternion).copyFrom(
      this.eye.rotationQuaternion as BABYLON.Quaternion
    )
    // Neck: the offset in the view's yaw frame (flat has no head roll to honour).
    BABYLON.Matrix.FromQuaternionToRef(
      this.eye.rotationQuaternion as BABYLON.Quaternion,
      this._m
    )
    BABYLON.Vector3.TransformCoordinatesToRef(this.neckOffset, this._m, this._v)
    this.neck.position.set(p.x + this._v.x, p.y + this._v.y, p.z + this._v.z)
    ;(this.neck.rotationQuaternion as BABYLON.Quaternion).copyFrom(
      this.eye.rotationQuaternion as BABYLON.Quaternion
    )
  }

  /** Head yaw in the rig's local frame (camera rotation is local to the rig). */
  private headLocalYaw(): number {
    const q = this.cam.rotationQuaternion ?? BABYLON.Quaternion.Identity()
    BABYLON.Matrix.FromQuaternionToRef(q, this._m)
    BABYLON.Vector3.TransformCoordinatesToRef(FORWARD, this._m, this._fwd)
    return Math.atan2(this._fwd.x, this._fwd.z)
  }

  /** Call once per XR frame. (Hands ride their grips by parenting, not here.) */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  update(dt: number): void {
    if (this.flatMode) {
      this.updateFlat()
      return
    }
    const cam = this.cam
    const headYaw = this.headLocalYaw()

    // Body: floor under the head, but RIG yaw — identity local rotation inherits
    // the rig's facing via parenting. So body-pinned panels stay put when you
    // glance to look at them and only swing when you actually turn (locomote),
    // matching the rig/overhead panels (the damped head-yaw chased them away).
    this.body.position.set(cam.position.x, 0, cam.position.z)
    // Eye: at the head POSITION, with rig yaw + the recenter correction, so HUD
    // panels anchored here ride the real eye (through chase head-compensation and
    // standing/sitting) AND align with where you actually look.
    this.eye.position.copyFrom(cam.position)
    BABYLON.Quaternion.RotationYawPitchRollToRef(
      this.eyeYawOffset,
      0,
      0,
      this.eye.rotationQuaternion as BABYLON.Quaternion
    )
    /*
    THE BODY TAKES THE RECENTER TOO. A piloting rig is turned by the head's
    yaw at entry, so the view looks along the craft; without the same
    correction here, "ahead of the body" was that far to one side. The death
    modal (on this frame) came up 70° away for someone who sat down 70° off
    the headset's own forward, and in a headset that reads as no modal at all.
    */
    ;(this.body.rotationQuaternion as BABYLON.Quaternion).copyFrom(
      this.eye.rotationQuaternion as BABYLON.Quaternion
    )

    // Neck: head pose pushed down + back to the pivot; yaw-only so UI pinned here
    // turns with you but you can tip your head to look past it.
    const q = cam.rotationQuaternion ?? BABYLON.Quaternion.Identity()
    BABYLON.Matrix.FromQuaternionToRef(q, this._m)
    BABYLON.Vector3.TransformCoordinatesToRef(this.neckOffset, this._m, this._v)
    this.neck.position.set(
      cam.position.x + this._v.x,
      cam.position.y + this._v.y,
      cam.position.z + this._v.z
    )
    BABYLON.Quaternion.RotationYawPitchRollToRef(
      headYaw,
      0,
      0,
      this.neck.rotationQuaternion as BABYLON.Quaternion
    )
  }

  dispose(): void {
    for (const { obs, cb } of this.inputObs) obs?.removeCallback?.(cb)
    this.inputObs = []
    this.world.dispose()
    this.eye.dispose()
    this.body.dispose()
    this.neck.dispose()
    this.face.dispose()
    this.leftHand.dispose()
    this.rightHand.dispose()
    if (this.ownsRig) this.rig.dispose()
  }
}

/**
 * An **entity / interlocutor** frame: pinned to a target node (an NPC, a vehicle,
 * a pickup) and turned to face the player, so its local −X is your screen-right
 * and +X your screen-left. The home of dialogue balloons, nameplates, lock-on
 * brackets — anything anchored to a *thing in the world you're attending to*.
 * Dynamic (one per target), so it's standalone rather than part of `XrFrames`.
 * Call `update(camera)` each frame; `dispose()` to release.
 */
export class EntityFrame {
  readonly node: BABYLON.TransformNode
  private target: BABYLON.TransformNode
  private offset: BABYLON.Vector3

  constructor(
    scene: BABYLON.Scene,
    target: BABYLON.TransformNode,
    opts: { offset?: [number, number, number] } = {}
  ) {
    this.target = target
    const o = opts.offset ?? [0, 0, 0]
    this.offset = new BABYLON.Vector3(o[0], o[1], o[2])
    this.node = new BABYLON.TransformNode('xr-entity-frame', scene)
    this.node.rotationQuaternion = new BABYLON.Quaternion()
  }

  update(cam: BABYLON.TargetCamera): void {
    const p = this.target.getAbsolutePosition()
    this.node.position.set(
      p.x + this.offset.x,
      p.y + this.offset.y,
      p.z + this.offset.z
    )
    const head = cam.globalPosition
    const yaw = facingYaw(this.node.position, head)
    BABYLON.Quaternion.RotationYawPitchRollToRef(
      yaw,
      0,
      0,
      this.node.rotationQuaternion as BABYLON.Quaternion
    )
  }

  dispose(): void {
    this.node.dispose()
  }
}
